// Mark'n'Post — Zod schema for the Georgia↔СНГ (РФ/БЕ/КЗ) order-creation form.
// One source of truth for "what a valid CIS request is", imported by both the
// mini-app form (client validation) and the API create route (server
// validation). Pure: no engine call, no Prisma, no encryption, no IO.
//
// Single source of truth: `category` validates against the frozen
// FLEX_CIS_CATEGORIES; name/address/phone delegate to the frozen cisFields
// predicates; the alcohol→KZ prohibition mirrors the pricing engine.
import { z } from "zod";
import { FLEX_CIS_CATEGORIES, } from "../flex/cis/pricing.js";
import { CIS_PHONE_DIGIT_COUNT, isCyrillicAddressText, isCyrillicName, isValidCisPhone, } from "./cisFields.js";
// Destination is a closed CIS set. Declared locally with a `satisfies` guard so
// an invalid code fails compile — no frozen runtime array for it exists, and
// exhaustiveness is otherwise guarded by the engine's own country handling.
const CIS_DESTINATION_COUNTRIES = [
    "RU",
    "BY",
    "KZ",
];
const _cisDestinationsExhaustive = true;
void _cisDestinationsExhaustive;
// ──────────────────────────────────────────────────────────────
// Field helpers (mirror the zod idiom of schemas.ts).
// ──────────────────────────────────────────────────────────────
/** Cyrillic name — delegates to the frozen `isCyrillicName` predicate. */
const cyrillicName = z.string().refine(isCyrillicName, "Имя должно быть на кириллице.");
/** Cyrillic address text — delegates to the frozen `isCyrillicAddressText`. */
const cyrillicAddressText = z
    .string()
    .refine(isCyrillicAddressText, "Адрес должен быть на кириллице.");
/** Loose phone — trim, 4..20, цифры/пробелы/-(). Mirrors Europe `phoneLocal`. */
const loosePhoneLocal = z
    .string()
    .trim()
    .min(4, "Слишком короткий номер.")
    .max(20)
    .regex(/^[0-9\- ()]+$/, "Только цифры, пробелы и -().");
/** Passport — soft format (trim, 3..40). Raw plaintext; encryption is the endpoint's job. */
const passportFormat = z.string().trim().min(3, "Серия и номер паспорта обязательны.").max(40);
/**
 * True iff `s` is a real calendar date in `YYYY-MM-DD` form (rejects e.g.
 * `2026-13-45`, `2026-02-31`). Pure and clock-free — it constructs a UTC Date
 * from the explicit components and checks they survive the round-trip, so it
 * depends on nothing but its input. Rejecting PAST dates needs the current day
 * and is the endpoint's job (gap B, Нико 2026-06-13).
 */
function isRealCalendarDate(s) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (!m)
        return false;
    const year = Number(m[1]);
    const month = Number(m[2]);
    const day = Number(m[3]);
    const dt = new Date(Date.UTC(year, month - 1, day));
    return dt.getUTCFullYear() === year && dt.getUTCMonth() === month - 1 && dt.getUTCDate() === day;
}
// ──────────────────────────────────────────────────────────────
// Place / Recipient sub-schemas.
// ──────────────────────────────────────────────────────────────
const placeSchema = z.object({
    // Single source of truth: validate against the frozen FLEX_CIS_CATEGORIES at
    // runtime AND narrow the inferred type to the 6-member `FlexCisCategory` union
    // (z.custom<T> yields output type T) — so consumers like computeFlexCisQuote
    // get the union, not a widened `string`, and exhaustive switches type-check.
    category: z.custom((val) => typeof val === "string" && FLEX_CIS_CATEGORIES.includes(val), { message: "Неизвестная категория." }),
    declaredValueGel: z
        .number({ invalid_type_error: "Введите стоимость числом." })
        .finite("Стоимость должна быть числом.")
        .min(0, "Стоимость не может быть отрицательной.")
        .max(1_000_000, "Слишком большая стоимость."),
    // Quantity is validated ONLY for the category-matching field, inside the
    // superRefine switch below (gap A, Нико 2026-06-13): a non-matching field —
    // including a literal 0 the form sends for unused inputs — is accepted and
    // ignored. So at field level these are just optional numbers; the matching
    // field gets its full presence/sign/integer/cap check in the switch.
    weightKg: z.number().optional(),
    documentsCount: z.number().optional(),
    packsCount: z.number().optional(),
    litres: z.number().optional(),
});
const recipientSchema = z.object({
    fullName: cyrillicName,
    deliveryType: z.enum(["pvz", "door"]),
    passport: passportFormat,
    missingPhone: z.boolean().default(false),
    missingAddress: z.boolean().default(false),
    // Accepted СНГ codes are single-sourced from the frozen CIS_PHONE_DIGIT_COUNT
    // map (not a hand-listed copy — architecture rule №1); isValidCisPhone
    // re-checks code + digit count in the superRefine.
    phoneCode: z
        .string()
        .refine((c) => CIS_PHONE_DIGIT_COUNT[c] !== undefined, "Неверный код страны для СНГ.")
        .optional(),
    phoneLocal: loosePhoneLocal.optional(),
    city: cyrillicAddressText.optional(),
    street: cyrillicAddressText.optional(),
    building: cyrillicAddressText.optional(),
    apartment: cyrillicAddressText.optional(),
    zip: cyrillicAddressText.optional(),
    places: z.array(placeSchema).min(1).max(20),
});
// ──────────────────────────────────────────────────────────────
// Order-level schema + the single top-level superRefine.
// ──────────────────────────────────────────────────────────────
export const flexCisRequestSchema = z
    .object({
    destinationCountry: z.enum(CIS_DESTINATION_COUNTRIES, {
        errorMap: () => ({ message: "Направление: РФ, БЕ или КЗ." }),
    }),
    senderFullName: cyrillicName,
    senderPhoneCode: z.string().regex(/^\+\d{1,4}$/, "Неверный код страны."),
    senderPhoneLocal: loosePhoneLocal,
    senderPassport: passportFormat,
    senderAddressLine: z.string().trim().max(500).optional(),
    selfDeliver: z.boolean(),
    // Pickup — optional at field level; conditionally required in superRefine.
    pickupCity: z.string().trim().max(120).optional(),
    pickupAddress: z.string().trim().max(500).optional(),
    pickupGeoUrl: z.string().trim().max(500).optional(),
    pickupFloor: z.number().int().min(-5).max(200).optional(),
    pickupHasElevator: z.boolean().optional(),
    pickupPhoneLocal: loosePhoneLocal.optional(),
    pickupSameAsSender: z.boolean().default(false),
    pickupDay: z.string().trim().max(50).optional(),
    pickupSlot: z.string().trim().max(50).optional(),
    recipients: z.array(recipientSchema).min(1).max(20),
    clientSelfShippedCdek: z.boolean().default(false),
    cdekTrackingNumber: z.string().trim().max(100).optional(),
    storageNeeded: z.boolean().default(false),
    // Validated (shape + real calendar date) only when storageNeeded — see the
    // superRefine (gap B). Ignored otherwise, consistent with gap A.
    storageUntil: z.string().optional(),
    prohibitedAck: z.literal(true, {
        errorMap: () => ({ message: "Подтвердите отсутствие запрещённых предметов." }),
    }),
    termsAck: z.literal(true, {
        errorMap: () => ({ message: "Подтвердите согласие с условиями." }),
    }),
})
    .superRefine((data, ctx) => {
    const destination = data.destinationCountry;
    // Recipients: per-recipient phone, address, and per-place quantity rules.
    data.recipients.forEach((recipient, i) => {
        // Phone required unless missingPhone.
        if (!recipient.missingPhone) {
            const code = recipient.phoneCode;
            const local = recipient.phoneLocal;
            const phoneOk = code !== undefined &&
                code in CIS_PHONE_DIGIT_COUNT &&
                local !== undefined &&
                isValidCisPhone(code, local);
            if (!phoneOk) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ["recipients", i, "phoneLocal"],
                    message: "Телефон получателя обязателен и должен быть корректным.",
                });
            }
        }
        // Address required unless missingAddress (apartment always optional).
        if (!recipient.missingAddress) {
            const required = [
                ["city", "Город обязателен."],
                ["street", "Улица обязательна."],
                ["building", "Дом обязателен."],
                ["zip", "Индекс обязателен."],
            ];
            for (const [key, msg] of required) {
                const value = recipient[key];
                if (typeof value !== "string" || value.trim().length === 0) {
                    ctx.addIssue({
                        code: z.ZodIssueCode.custom,
                        path: ["recipients", i, key],
                        message: msg,
                    });
                }
            }
        }
        // Per-place: category-specific required quantity + alcohol→KZ prohibition.
        recipient.places.forEach((place, j) => {
            const placePath = ["recipients", i, "places", j];
            // Alcohol to Kazakhstan is rejected outright (mirrors the engine flag
            // `kz_alcohol_prohibited`), regardless of litres.
            if (place.category === "alcohol" && destination === "KZ") {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: [...placePath, "category"],
                    message: "Алкоголь в Казахстан не возим.",
                });
                return;
            }
            // Category-matching quantity (mirrors the engine `computeBase`).
            switch (place.category) {
                case "personal": {
                    if (typeof place.weightKg !== "number" ||
                        !Number.isFinite(place.weightKg) ||
                        place.weightKg <= 0 ||
                        place.weightKg > 1000) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: [...placePath, "weightKg"],
                            message: "Укажите вес (кг).",
                        });
                    }
                    break;
                }
                case "documents": {
                    if (typeof place.documentsCount !== "number" ||
                        !Number.isInteger(place.documentsCount) ||
                        place.documentsCount < 1 ||
                        place.documentsCount > 10_000) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: [...placePath, "documentsCount"],
                            message: "Укажите количество документов.",
                        });
                    }
                    break;
                }
                case "medicine": {
                    if (typeof place.packsCount !== "number" ||
                        !Number.isInteger(place.packsCount) ||
                        place.packsCount < 1 ||
                        place.packsCount > 10_000) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: [...placePath, "packsCount"],
                            message: "Укажите количество упаковок.",
                        });
                    }
                    break;
                }
                case "alcohol": {
                    // Destination RU/BY only here (KZ handled above).
                    if (typeof place.litres !== "number" ||
                        !Number.isFinite(place.litres) ||
                        place.litres <= 0 ||
                        place.litres > 1000) {
                        ctx.addIssue({
                            code: z.ZodIssueCode.custom,
                            path: [...placePath, "litres"],
                            message: "Укажите объём (л).",
                        });
                    }
                    break;
                }
                // appliances / digital: manual pricing — no quantity required.
            }
        });
    });
    // Cheap СДЭК mode is RU-only and needs a tracking number.
    if (data.clientSelfShippedCdek === true) {
        if (destination !== "RU") {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["clientSelfShippedCdek"],
                message: "Дешёвый режим СДЭК доступен только для РФ.",
            });
        }
        const track = data.cdekTrackingNumber;
        if (typeof track !== "string" || track.trim().length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["cdekTrackingNumber"],
                message: "Укажите трек-номер СДЭК.",
            });
        }
    }
    // Courier pickup needs a city and address.
    if (!data.selfDeliver) {
        if (typeof data.pickupCity !== "string" || data.pickupCity.trim().length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["pickupCity"],
                message: "Укажите город забора.",
            });
        }
        if (typeof data.pickupAddress !== "string" || data.pickupAddress.trim().length === 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["pickupAddress"],
                message: "Укажите адрес забора.",
            });
        }
    }
    // Storage needs a date.
    if (data.storageNeeded === true) {
        if (typeof data.storageUntil !== "string" || !isRealCalendarDate(data.storageUntil)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["storageUntil"],
                message: "Укажите корректную дату (ГГГГ-ММ-ДД), до которой хранить.",
            });
        }
    }
});
//# sourceMappingURL=cisRequest.js.map